"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { AnimatePresence, motion } from "motion/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../lib/cn";
import { springs, durations, easings } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { usePagination, type PaginationItem } from "../hooks/usePagination";
import {
  PaginationContent,
  PaginationItem as PaginationItemSlot,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "../ui/pagination";
import { buttonVariants } from "../ui/button";

// The stock `Pagination` is a styled <nav> that is not ref-forwarding, so we
// reproduce its exact wrapper here to keep `forwardRef` working on a real DOM
// node (matching the compact branch) while still composing every meaningful
// stock primitive below it.
const PAGINATION_NAV_CLASS = "mx-auto flex w-full justify-center";

/**
 * Compact-variant pill sizing/tone, driven through `cva` rather than the
 * donor's inline hex so the navy pill follows brand tokens (active
 * `bg-primary text-primary-foreground`, idle `text-muted-foreground` on
 * `bg-background` with a `border-border` ring and a `hover:bg-muted` tint).
 */
const compactPillVariants = cva(
  [
    "inline-flex items-center justify-center rounded-full border text-sm font-medium",
    "transition-colors duration-150 ease-[ease]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
    "disabled:cursor-not-allowed disabled:opacity-40",
  ].join(" "),
  {
    variants: {
      size: {
        sm: "size-7",
        md: "size-8",
      },
      tone: {
        active: "border-transparent bg-primary text-primary-foreground shadow-sm",
        idle: "border-border bg-background text-muted-foreground hover:bg-muted",
      },
    },
    defaultVariants: { size: "md", tone: "idle" },
  },
);

export interface SmartPaginationProps
  extends Omit<React.ComponentProps<"nav">, "onChange">,
    Pick<VariantProps<typeof compactPillVariants>, "size"> {
  /** Currently active page (1-based). */
  currentPage: number;
  /** Total number of pages. */
  totalPages: number;
  /** Fired with the next page. Never fires for the page already active. */
  onPageChange: (page: number) => void;
  /**
   * Layout. `"sliding"` (default) is the full pinned-window pager built on the
   * stock primitives + `PaginationEllipsis`; `"compact"` is the two-page navy
   * pill row from FE-React's `CompactPagination`, re-skinned to tokens.
   */
  variant?: "sliding" | "compact";
  /** Page numbers shown each side of current (sliding only). Default `1`. */
  siblingCount?: number;
  /** Pages pinned at each end (sliding only). Default `1`. */
  boundaryCount?: number;
  /**
   * Scroll the window to the top after a page change. Default `false` and
   * SSR-safe: the scroll runs in an effect after commit, guarded on `window`
   * and forced to `behavior: "auto"` under reduced-motion.
   */
  scrollToTopOnChange?: boolean;
  /**
   * Render a page link as a custom element (e.g. a router `<Link>`). Receives
   * the resolved 1-based page and the inner children; the returned node is
   * dropped into the stock `PaginationLink` via `asChild`. Sliding variant
   * only. When omitted, an `<a href="#">` with a click handler is used.
   */
  renderLink?: (page: number, children: React.ReactNode) => React.ReactNode;
}

/**
 * SmartPagination — the smart windowing layer over the stock `@aisquare/ui`
 * `Pagination` primitives. Consumes {@link usePagination} for the sliding-window
 * math (first/last pin, edge-expansion, gap-aware ellipsis) and renders the
 * on-brand stock parts, so a dev passing only `currentPage` / `totalPages` /
 * `onPageChange` gets a polished, a11y-correct pager.
 *
 * Exported as `SmartPagination` (not `Pagination`) so it does not collide with
 * the stock `Pagination` symbol re-exported through `@aisquare/ui/stock`.
 *
 * Behaviour ported from FE-React's `SlidingPagination` + `CompactPagination`:
 * - Self-hides when `totalPages <= 1`.
 * - Re-clicking the active page is a no-op (never fires `onPageChange`).
 * - Prev / Next are guarded at the edges and rendered disabled there.
 * - `variant="sliding"` animates the active marker between buttons with a
 *   shared-layout `layoutId` and fades window entries via `AnimatePresence`;
 *   `variant="compact"` is the two-window navy pill row.
 *
 * Motion uses the lib presets ({@link springs} for the slide, {@link durations}
 * / {@link easings} for fades) and is fully gated on
 * {@link useReducedMotion} — when reduced, the `layoutId` slide and the
 * `AnimatePresence` transitions are dropped (static render) and the opt-in
 * scroll uses `behavior: "auto"`.
 *
 * @example
 *   <SmartPagination currentPage={page} totalPages={20} onPageChange={setPage} />
 *   <SmartPagination variant="compact" currentPage={page} totalPages={6} onPageChange={setPage} />
 */
export const SmartPagination = React.forwardRef<HTMLElement, SmartPaginationProps>(
  (
    {
      currentPage,
      totalPages,
      onPageChange,
      variant = "sliding",
      siblingCount = 1,
      boundaryCount = 1,
      scrollToTopOnChange = false,
      size,
      renderLink,
      className,
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion() ?? false;
    const animate = !reduced;
    // Stable per-instance id so two pagers on a page never cross-animate their
    // shared-layout active markers.
    const layoutId = React.useId();

    // Unified guard (the SlidingPagination rule): re-clicking the active page
    // is a no-op, so CompactPagination's prev/next don't fire on the edges
    // either. Tracks whether a change actually happened to drive the opt-in
    // scroll effect only on a real navigation.
    const lastNavigated = React.useRef<number | null>(null);
    const goTo = React.useCallback(
      (next: number) => {
        if (next < 1 || next > totalPages || next === currentPage) return;
        lastNavigated.current = next;
        onPageChange(next);
      },
      [currentPage, totalPages, onPageChange],
    );

    // Opt-in scroll-to-top, decoupled from render: run after the page commit,
    // SSR-guarded, and only when this component drove the change.
    React.useEffect(() => {
      if (!scrollToTopOnChange) return;
      if (lastNavigated.current !== currentPage) return;
      lastNavigated.current = null;
      if (typeof window === "undefined") return;
      window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
    }, [currentPage, scrollToTopOnChange, reduced]);

    const items = usePagination({
      currentPage,
      totalPages,
      siblingCount,
      boundaryCount,
    });

    // Self-hide: nothing to page through. `usePagination` returns [] here, but
    // guard explicitly so the compact branch (which doesn't read `items`)
    // hides too and no empty <nav> renders.
    if (totalPages <= 1) return null;

    const atStart = currentPage <= 1;
    const atEnd = currentPage >= totalPages;

    if (variant === "compact") {
      // Two-window math from CompactPagination: current + neighbour, sliding
      // back by one only on the last page.
      const startPage =
        currentPage === totalPages ? Math.max(1, totalPages - 1) : currentPage;
      const visiblePages = Array.from(
        new Set([startPage, Math.min(totalPages, startPage + 1)]),
      ).sort((a, b) => a - b);

      return (
        <nav
          ref={ref}
          role="navigation"
          aria-label="pagination"
          data-slot="smart-pagination"
          className={cn("flex items-center justify-center gap-2", className)}
          {...rest}
        >
          <button
            type="button"
            onClick={() => goTo(currentPage - 1)}
            disabled={atStart}
            className={cn(compactPillVariants({ size, tone: "idle" }), "shadow-sm")}
            aria-label="Previous page"
          >
            <span className="sr-only">Previous</span>
            <ChevronLeft className="size-3.5" aria-hidden />
          </button>

          {visiblePages.map((p) => {
            const isActive = p === currentPage;
            // When animating, the moving navy `motion.span` is the only fill,
            // so the button stays idle-toned (transparent over the span) with
            // white text. When reduced, fall back to the static filled tone.
            const tone = isActive && !animate ? "active" : "idle";
            const pill = (
              <button
                type="button"
                onClick={() => goTo(p)}
                className={cn(
                  compactPillVariants({ size, tone }),
                  "relative",
                  isActive && animate && "border-transparent text-primary-foreground hover:bg-transparent",
                )}
                aria-current={isActive ? "page" : undefined}
                aria-label={`Page ${p}`}
              >
                {animate && isActive ? (
                  <motion.span
                    layoutId={`${layoutId}-active`}
                    aria-hidden
                    className="absolute inset-0 -z-10 rounded-full bg-primary shadow-sm"
                    transition={springs.gentle}
                  />
                ) : null}
                {p}
              </button>
            );
            return <React.Fragment key={p}>{pill}</React.Fragment>;
          })}

          <button
            type="button"
            onClick={() => goTo(currentPage + 1)}
            disabled={atEnd}
            className={cn(compactPillVariants({ size, tone: "idle" }), "shadow-sm")}
            aria-label="Next page"
          >
            <span className="sr-only">Next</span>
            <ChevronRight className="size-3.5" aria-hidden />
          </button>
        </nav>
      );
    }

    // --- sliding variant ---
    const renderPageLink = (item: Extract<PaginationItem, { type: "page" }>) => {
      const inner = (
        <>
          {animate && item.active ? (
            <motion.span
              layoutId={`${layoutId}-active`}
              aria-hidden
              className="absolute inset-0 -z-10 rounded-md border border-border bg-background shadow-xs"
              transition={springs.gentle}
            />
          ) : null}
          <span className="relative z-10">{item.page}</span>
        </>
      );

      // When animating, the sliding `motion.span` is the active marker, so
      // strip the stock outline's border/fill on the active link (leaving it
      // ghost) to avoid a static second outline competing with the slide.
      // When reduced, the stock `isActive` outline stands on its own.
      const linkClass = cn(
        "relative",
        item.active && animate && "border-transparent bg-transparent shadow-none hover:bg-transparent",
      );

      if (renderLink) {
        // The stock PaginationLink renders a bare <a> with no asChild slot, so
        // to let a consumer swap in a router <Link> we reproduce its styling +
        // a11y attrs and merge them onto their element via cloneElement.
        const node = renderLink(item.page, inner);
        if (!React.isValidElement(node)) return node;
        const nodeProps = node.props as { className?: string };
        return React.cloneElement(node as React.ReactElement<Record<string, unknown>>, {
          "aria-current": item.active ? "page" : undefined,
          "data-slot": "pagination-link",
          "data-active": item.active,
          className: cn(
            buttonVariants({ variant: item.active ? "outline" : "ghost", size: "icon" }),
            linkClass,
            nodeProps.className,
          ),
        });
      }

      return (
        <PaginationLink
          href="#"
          isActive={item.active}
          className={linkClass}
          onClick={(e) => {
            e.preventDefault();
            goTo(item.page);
          }}
        >
          {inner}
        </PaginationLink>
      );
    };

    return (
      <nav
        ref={ref}
        role="navigation"
        aria-label="pagination"
        data-slot="smart-pagination"
        className={cn(PAGINATION_NAV_CLASS, className)}
        {...rest}
      >
        <PaginationContent>
          <PaginationItemSlot>
            <PaginationPrevious
              href="#"
              aria-disabled={atStart}
              className={cn(atStart && "pointer-events-none opacity-50")}
              onClick={(e) => {
                e.preventDefault();
                goTo(currentPage - 1);
              }}
            />
          </PaginationItemSlot>

          <AnimatePresence initial={false} mode="popLayout">
            {items.map((item) => {
              const key = item.type === "page" ? `page-${item.page}` : item.key;
              const transition = animate
                ? { duration: durations.hover, ease: easings.smoothOut }
                : { duration: 0 };
              return (
                <motion.li
                  key={key}
                  data-slot="pagination-item"
                  layout={animate}
                  initial={animate ? { opacity: 0, scale: 0.85 } : false}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={animate ? { opacity: 0, scale: 0.85 } : { opacity: 1 }}
                  transition={transition}
                >
                  {item.type === "page" ? (
                    renderPageLink(item)
                  ) : (
                    <PaginationEllipsis />
                  )}
                </motion.li>
              );
            })}
          </AnimatePresence>

          <PaginationItemSlot>
            <PaginationNext
              href="#"
              aria-disabled={atEnd}
              className={cn(atEnd && "pointer-events-none opacity-50")}
              onClick={(e) => {
                e.preventDefault();
                goTo(currentPage + 1);
              }}
            />
          </PaginationItemSlot>
        </PaginationContent>
      </nav>
    );
  },
);
SmartPagination.displayName = "SmartPagination";
